import { BaseSyntheticEvent, useEffect, useState } from "react";
import { useOutletContext, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { archiveProject, deleteProject } from "../../../core/services/gutcheck-api.service";
import { DeleteProjectButton, ProjectsContainer, RevisionContainer, OverviewContainer, SuggestionsContainer, InsightsContainer, AiRevisionContainer } from "../components/project_overview_components";
import { ProjectHeader } from "../components/base_project_component";
import { FormControl, FormControlLabel, Switch, Box, Container } from "@mui/material";
import { DocumentTitle } from "../../../components/DocumentTitle";
import useAnalytics from "../../../hooks/useAnalytics";
import { Sidebar, SidebarSection } from "../../../components/Sidebar";
import { 
  Home, 
  Users, 
  BarChart3, 
  TrendingUp, 
  Lightbulb, 
  Edit, 
  Wand2, 
  Settings 
} from "lucide-react";


type TProjectOverview = {
    highlightedPhrases: string[],
    highlightedTitle: string,
    highlightedIndex: string,
    tab: string,
    revisionTab: string
};

const initState: TProjectOverview = {
  highlightedPhrases: [],
  highlightedTitle: "",
  highlightedIndex: "",
  tab: "overview",
  revisionTab: "Predicted Performance"
};

type TProjectRespondant = {
  id: string,
  isOpen: boolean
};

const initRespondentState: TProjectRespondant = {
  id: "",
  isOpen: false
};

const allowedGuestTabs = ["viewpoints", "analysis"];

export const ProjectOverview = () => {

  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  let [searchParams, setSearchParams] = useSearchParams();
  const { trackPage } = useAnalytics();

  const {project, organization, aiResults, viewerRole}  = useOutletContext<{project: any, organization: any, viewerRole: any, aiResults: any}>();
  const [currentRole, setCurrentRole] = useState(viewerRole);

  const defaultTab = currentRole === "guest" ? "viewpoints" : "overview";

  const [state, setState] = useState(initState);

  const [respondent, setRespondent] = useState(initRespondentState);

  const [summaryPagination, setSummaryPagination] = useState({
    numPages: null,
    currentPage: 1
  });

  const [assetPagination, setAssetPagination] = useState({
    numPages: null,
    currentPage: 1
  });


  useEffect(() => {
    const tab = searchParams.get('tab') && (searchParams.get('tab') !== "") ? searchParams.get('tab') : defaultTab;
    trackPage(`deal page - ${tab}`, { organization: organization.tag, project: project.tag, role: currentRole })
  }, []);

  useEffect(() => {
    let tab = searchParams.get('tab') && (searchParams.get('tab') !== "") ? searchParams.get('tab') : defaultTab;
    if (tab && currentRole === "guest") {
      tab = allowedGuestTabs.includes(tab) ? tab : "viewpoints";
    }
    setState({
      ...state,
      tab: tab ?? defaultTab});
  }, [searchParams, currentRole]);


  const participant = searchParams.get('pid') ?? "";
  if (participant !== respondent.id) {
    setRespondent({
      id: participant,
      isOpen: participant !== "" ? true : false
    });
    if (participant) {
      trackPage(`deal page - viewpoint`, { organization: organization.tag, project: project.tag, role: currentRole, viewpoint: participant });
    }
  }

  const handleCreateIteration = () => { 
    const newIterationURL = `/project/new/${organization.tag}/${project.tag}`;
    return navigate(newIterationURL, { state: { organization: organization, project: project } }); 
  };

  const handleDelete = async (event: any) => {
      event.preventDefault();
      const accessToken = await getAccessTokenSilently();

      const { error } = await deleteProject(project.id, accessToken);

      if (error) throw error;

      return navigate(`/o/${organization.tag}/`);
  };

  const handleArchive = async (event: any) => {
      event.preventDefault();
      const accessToken = await getAccessTokenSilently();

      const { error } = await archiveProject(project.id, accessToken);
      if (error) throw error;

      return navigate(`/o/${organization.tag}/`);
  };
  
  const handleRevisionMouse = (event: any, action: any) => {
    if (action === "over") {
      const revisionTitle = event.currentTarget.getElementsByClassName("revision-title")[0].innerText;
      const revisionIndex = event.currentTarget.id;
      const revision = /(```json|```)/.test(aiResults?.revisions[aiResults.revisions.length - 1]) ? 
        JSON.parse(aiResults.revisions[aiResults.revisions.length - 1]?.replace(/(```json|```)/g, "")).revisions.find( (revision: any) => revision.title === revisionTitle)
        : JSON.parse(project.iterations.at(-1).revision_result_json).revisions.find( (revision: any) => revision.title === revisionTitle)


      setState({
        ...state,
        highlightedPhrases: revision.phrases,
        highlightedTitle: revisionTitle,
        highlightedIndex: revisionIndex
      });
    } else if (state.highlightedPhrases.length !== 0) {
      setState({
        ...state,
        highlightedPhrases: [],
        highlightedTitle: "",
        highlightedIndex: ""
      });
    };
  };  

  const handleSwitchTab = (event: any) => {
    if (!event.currentTarget.getAttribute("data-name")) return;
    const tab = event.currentTarget.getAttribute("data-name").toLowerCase() === "overview" ? "" : event.currentTarget.getAttribute("data-name").toLowerCase();
    setSearchParams({
      ...searchParams,
      tab: tab
    });

    trackPage(`deal page - ${tab}`, { organization: organization.tag, project: project.tag, role: currentRole} );
  };

  const handleSwitchRevisionTab = (event: any) => {
    setState({
      ...state,
      revisionTab: event.target.textContent
    });
  };

  const handleRespondant = (event: BaseSyntheticEvent) => {
    if (!event.target.id) {
      setSearchParams({
        ...searchParams,
        tab: "viewpoints",
        pid: ""
      });
    } else {
      setSearchParams({
        ...searchParams,
        tab: "viewpoints",
        pid: event.target.id
      });
    }
  };

  const handleSummaryLoad = ( {numPages}: any ) => {
    setSummaryPagination({
      ...summaryPagination,
      numPages: numPages,
      currentPage: 1
    });
  };
  
  const handleSummaryChange = (event: any, offset: any) => {
    setSummaryPagination({
      ...summaryPagination,
      currentPage: summaryPagination.currentPage + offset
    });
  };

  const handleAssetLoad = ( {numPages}: any ) => {
    setAssetPagination({
      ...assetPagination,
      numPages: numPages,
      currentPage: 1
    });
  };
  
  const handleAssetChange = (event: any, offset: any) => {
    setAssetPagination({
      ...assetPagination,
      currentPage: assetPagination.currentPage + offset
    });
  };

  const handleRoleSwitch = () => {
    if (currentRole === "guest") {
      setCurrentRole("contributor");
    } else {
      setCurrentRole("guest");
    }
    searchParams.delete("tab");
    setSearchParams(searchParams);
  }

  const handleAdminRoleSwitch = (event: any) => {
    setCurrentRole(event.target.value);
  }

  // Create sections for the generic Sidebar component
  const createSidebarSections = (): SidebarSection[] => {
    const isGuest = currentRole === "guest";
    const isContributor = currentRole === "contributor" || currentRole === "admin";
    const projectType = project.user_type === "investor" || project.user_type === "entrepreneur" ? "investor" : "marketing";
    
    // Check what tabs should be enabled
    const hasSummary = project.iterations?.at(-1)?.summary_url ? true : false;
    const hasRespondents = project.viewpoints?.length > 0;
    const hasAnalysis = project.iterations?.at(-1)?.analysis_url ? true : false;
    const hasInsights = aiResults?.insights && aiResults?.insights !== "";
    const hasSuggestions = aiResults?.suggestions && aiResults?.suggestions !== "";
    const hasRevision = (project.iterations?.at(-1)?.revision_text || /(```json|```)/.test(aiResults?.revisions[aiResults?.revisions?.length - 1])) ? true : false;

    const sections: SidebarSection[] = [];

    // Create a custom component for each tab that handles click events
    const createTabComponent = (tabName: string, icon: React.ReactNode, enabled: boolean) => {
      const isActive = state.tab.toLowerCase() === tabName.toLowerCase() || 
                     (tabName === "Overview" && state.tab === "overview") ||
                     (tabName === "Overview" && state.tab === "");
      
      return (
        <div
          onClick={enabled ? () => handleSidebarTabClick(tabName) : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px',
            cursor: enabled ? 'pointer' : 'not-allowed',
            backgroundColor: isActive ? '#667788' : 'transparent',
            color: isActive ? '#ffffff' : enabled ? 'rgba(0, 0, 0, 0.8)' : '#667788',
            borderRadius: '5px',
            marginBottom: '5px',
            fontWeight: 600,
            opacity: enabled ? 1 : 0.6
          }}
        >
          {icon}
          <span style={{ marginLeft: '8px' }}>{tabName}</span>
        </div>
      );
    };

    if (isGuest) {
      // Guest sections
      sections.push({
        visible: true,
        links: [
          {
            to: "#",
            component: createTabComponent("Viewpoints", <Users size={16} />, hasRespondents),
            text: "Viewpoints",
            enabled: hasRespondents,
            visible: true,
            end: false
          },
          {
            to: "#",
            component: createTabComponent("Analysis", <BarChart3 size={16} />, hasAnalysis),
            text: "Analysis",
            enabled: hasAnalysis,
            visible: true,
            end: false
          }
        ]
      });
    } else if (isContributor) {
      // Main navigation section
      const links = [
        {
          to: "#",
          component: createTabComponent("Overview", <Home size={16} />, true),
          text: "Overview",
          enabled: true,
          visible: true,
          end: false
        },
        {
          to: "#",
          component: createTabComponent("Viewpoints", <Users size={16} />, hasRespondents),
          text: "Viewpoints",
          enabled: hasRespondents,
          visible: true,
          end: false
        },
        {
          to: "#",
          component: createTabComponent("Analysis", <BarChart3 size={16} />, hasAnalysis),
          text: "Analysis",
          enabled: hasAnalysis,
          visible: true,
          end: false
        }
      ];

      // Add Exec Summary for non-marketing projects
      if (projectType !== "marketing") {
        links.push({
          to: "#",
          component: createTabComponent("Exec Summary", <TrendingUp size={16} />, hasSummary),
          text: "Exec Summary",
          enabled: hasSummary,
          visible: true,
          end: false
        });
      }

      // Add marketing-specific tabs
      if (projectType === "marketing") {
        links.push(
          {
            to: "#",
            component: createTabComponent("Insights", <Lightbulb size={16} />, hasInsights),
            text: "Insights",
            enabled: hasInsights,
            visible: true,
            end: false
          },
          {
            to: "#",
            component: createTabComponent("Suggestions", <Edit size={16} />, hasSuggestions),
            text: "Suggestions",
            enabled: hasSuggestions,
            visible: true,
            end: false
          },
          {
            to: "#",
            component: createTabComponent("Revision", <Wand2 size={16} />, hasRevision),
            text: "Revision",
            enabled: hasRevision,
            visible: true,
            end: false
          }
        );
      }

      // Add Deal Setup
      links.push({
        to: "#",
        component: createTabComponent("Deal Setup", <Settings size={16} />, true),
        text: "Deal Setup",
        enabled: true,
        visible: true,
        end: false
      });

      sections.push({
        visible: true,
        links: links
      });
    }

    return sections;
  };

  // Handle tab switching for the new sidebar
  const handleSidebarTabClick = (tabName: string) => {
    const tab = tabName.toLowerCase() === "overview" ? "" : tabName.toLowerCase();
    setSearchParams({
      ...searchParams,
      tab: tab
    });
    trackPage(`deal page - ${tab || "overview"}`, { organization: organization.tag, project: project.tag, role: currentRole });
  };

  // Create bottom row content
  const createBottomRow = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {viewerRole !== "guest" && (
        <FormControl className="guest-toggle">
          <FormControlLabel 
            className="guest-toggle-label" 
            control={<Switch checked={currentRole === "guest"} onChange={handleRoleSwitch} />} 
            label="View as Guest" 
          />
        </FormControl>
      )}
      {false && viewerRole === "admin" && (
        <FormControl className="role-toggle">
          <select name="admin-role-select" value={currentRole} onChange={handleAdminRoleSwitch}>
            <option value="admin">Admin</option>
            <option value="contributor">Team</option>
            <option value="guest">Guest</option>
          </select>
        </FormControl>
      )}
    </Box>
  );

  return (
    <>
      <DocumentTitle pageTitle={project.name} />
      <Sidebar 
        sections={createSidebarSections()}
        bottomRow={createBottomRow()}
        className="projects-sidebar"
      />
      <Container component="main">
        <div className={`gc_view tab-${state.tab}`}>
          <ProjectHeader project={project} currentRole={currentRole} viewerRole={viewerRole} />
          <div className="project-content">
            { state.tab === "revision" && aiResults?.revisions && /(```json|```)/.test(aiResults.revisions[aiResults.revisions?.length - 1]) ?
                <AiRevisionContainer project={project} aiResults={aiResults} tab={state.revisionTab} highlights={state.highlightedPhrases} highlightedTitle={state.highlightedTitle} highlightedIndex={state.highlightedIndex} handleRevisionMouse={handleRevisionMouse} handleCreateIteration={handleCreateIteration} handleSwitchTab={handleSwitchRevisionTab} assetPagination={assetPagination} handleAssetLoad={handleAssetLoad} handleAssetChange={handleAssetChange}/>
                : state.tab === "revision" ?
                <RevisionContainer project={project} tab={state.revisionTab} highlights={state.highlightedPhrases} highlightedTitle={state.highlightedTitle} highlightedIndex={state.highlightedIndex} handleRevisionMouse={handleRevisionMouse} handleCreateIteration={handleCreateIteration} handleSwitchTab={handleSwitchRevisionTab} />
                : state.tab === "insights" ?
                <InsightsContainer project={project} assetPagination={assetPagination} handleAssetLoad={handleAssetLoad} handleAssetChange={handleAssetChange} aiResults={aiResults}/>
                : state.tab === "suggestions" ?
                <SuggestionsContainer project={project} assetPagination={assetPagination} handleAssetLoad={handleAssetLoad} handleAssetChange={handleAssetChange} aiResults={aiResults}/>
                :
                <OverviewContainer project={project} viewerRole={currentRole} tab={state.tab} respondent={respondent} handleRespondant={handleRespondant} handleSwitchTab={handleSwitchTab} summaryPagination={summaryPagination} handleSummaryLoad={handleSummaryLoad} handleSummaryChange={handleSummaryChange} assetPagination={assetPagination} handleAssetLoad={handleAssetLoad} handleAssetChange={handleAssetChange}/>
            }
            {/*<DeleteProjectButton status={project.status} handleArchive={handleArchive} handleDelete={handleDelete} />*/}
          </div>
        </div>
      </Container>
    </>
  );
};
